import { useMutation, useQuery } from '@tanstack/react-query';
import { TRPCClientError } from '@trpc/client';
import { useEffect, useRef, useState } from 'react';
import { App } from '../App.tsx';
import { errorText, useTRPC } from '../api.ts';
import { type AuthMode, AuthPage } from './AuthPage.tsx';
import { Landing } from './Landing.tsx';
import { VpnStatusPage } from './VpnStatusPage.tsx';
import './web.css';
import './landing.css';
import './dashboard.css';

const PENDING_KEY = 'uplink.pendingDomain';

/** Takes a one-time link parameter (?verify=, ?reset=) out of the address bar once read. */
function takeParam(name: string): string | null {
  const url = new URL(window.location.href);
  const value = url.searchParams.get(name);
  if (value) {
    url.searchParams.delete(name);
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  }
  return value;
}

function readPending(): string | null {
  try {
    return sessionStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
}

/**
 * The website: guests get the landing page and the sign-in pages; a signed-in person gets the same app as
 * in Telegram, laid out for a desktop. A domain picked before signing up opens in the app afterwards.
 */
export function WebRoot() {
  const trpc = useTRPC();
  const [trialSignup] = useState(() => takeParam('trial') === '1');
  const [resetToken] = useState(() => takeParam('reset'));
  const [verifyToken] = useState(() => takeParam('verify'));
  const [mode, setMode] = useState<AuthMode | null>(resetToken ? 'reset' : trialSignup ? 'signup' : null);
  const [pending, setPending] = useState(readPending);
  const me = useQuery({ ...trpc.me.queryOptions(), retry: false });
  const config = useQuery(trpc.config.queryOptions());

  const verify = useMutation(trpc.auth.verifyEmail.mutationOptions());
  const verified = useRef(false);
  useEffect(() => {
    if (verifyToken && !verified.current) {
      verified.current = true;
      verify.mutate({ token: verifyToken });
    }
  }, [verifyToken, verify]);

  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  if (pathname === '/vpn-status') return <VpnStatusPage />;

  const banner = verify.isSuccess
    ? { tone: 'ok', text: `Email ${verify.data.email ?? ''} confirmed.` }
    : verify.isError
      ? { tone: 'crit', text: errorText(verify.error) }
      : null;
  const bannerEl = banner ? (
    <div className={`ul-web-banner ul-web-banner--${banner.tone}`} role="status">
      {banner.text}
    </div>
  ) : null;

  const signedOut = me.isError && me.error instanceof TRPCClientError && me.error.data?.httpStatus === 401;

  if (me.data && mode !== 'reset') {
    if (trialSignup) {
      window.location.replace('/?s=vpnTrial');
      return <div className="ul-land" aria-busy="true" />;
    }
    // A domain chosen on the landing page: open the app on its search
    if (pending) {
      try {
        sessionStorage.removeItem(PENDING_KEY);
      } catch {}
      window.history.replaceState(null, '', `/?s=search.${encodeURIComponent(pending)}`);
      setPending(null);
    }
    return (
      <>
        {bannerEl}
        <App web />
      </>
    );
  }
  if (me.isPending) return <div className="ul-land" aria-busy="true" />;
  if (!signedOut && me.isError) {
    return (
      <div className="ul-land">
        <div className="ul-land__wrap ul-land__fail" role="alert">
          {errorText(me.error)}
        </div>
      </div>
    );
  }

  const botUrl = config.data?.botUrl ?? null;
  return (
    <>
      {bannerEl}
      {mode ? (
        <AuthPage
          mode={mode}
          setMode={setMode}
          resetToken={resetToken}
          pendingDomain={pending}
          onHome={() => setMode(null)}
        />
      ) : (
        <Landing
          botUrl={botUrl}
          onSignIn={() => setMode('signin')}
          onSignUp={(domain) => {
            if (domain) {
              try {
                sessionStorage.setItem(PENDING_KEY, domain);
              } catch {}
              setPending(domain);
            }
            setMode('signup');
          }}
        />
      )}
    </>
  );
}
